/* =============================================
   TestPage.jsx — TANBIO
   Route: /tests/:slug
   Fixes:
   - All observers keyed on `service.id` / `others`
     so they re-run on every slug change
   - itemRefs array cleared on each render cycle
   - TestOthers keyed on slug so it fully remounts
   - rootMargin removed; threshold lowered to 0.05
   - scroll is instant before navigate, not smooth
   - Helmet added for per-page SEO meta
   ============================================= */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import SERVICES from "../data/services.data";
import "../styles/TestPage.css";

/* ─────────────────────────────────────────────
   Root page
───────────────────────────────────────────── */
export default function TestPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service  = SERVICES.find((s) => s.slug === slug);

  // Scroll to top on every slug change — instant so
  // the new content is never seen mid-scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="tp-notfound">
        <Container>
          <p>Service not found.</p>
          <button className="tp-back-btn tp-back-btn--in" onClick={() => navigate("/service")}>
            <i className="fa-solid fa-arrow-left me-2" />
            Back to Services
          </button>
        </Container>
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.id !== service.id);

  return (
    <div className="tp-page">
      <Helmet>
        <title>{service.title} | TanBio BioMed Genomics Laboratory</title>
        <meta
          name="description"
          content={`${service.title} testing available at TanBio BioMed Genomics Laboratory with accurate and reliable laboratory reports.`}
        />
        <link
          rel="canonical"
          href={`https://tanbiomedlab.com/tests/${slug}`}
        />
      </Helmet>

      {/* key=slug forces full remount of hero on slug change */}
      <TestHero key={`hero-${slug}`} service={service} />
      <TestDetail key={`detail-${slug}`} service={service} />
      {/* key=slug forces TestOthers to fully remount — clears
          all observer state and re-renders the correct 6 cards */}
      <TestOthers key={`others-${slug}`} others={others} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   1. TestHero
───────────────────────────────────────────── */
function TestHero({ service }) {
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Double-rAF: ensures the browser has painted the
    // initial (opacity:0) state before we add --in
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setMounted(true))
    );
    return () => cancelAnimationFrame(id);
  }, []); // runs once per mount (key=slug guarantees remount)

  return (
    <section
      className="tp-hero"
      style={{ "--tp-hero-img": `url(${service.image})` }}
    >
      <div className="tp-hero__overlay" />

      <Container fluid="lg" className="tp-hero__container">
        <button
          className={`tp-back-btn${mounted ? " tp-back-btn--in" : ""}`}
          onClick={() => navigate("/service")}
        >
          <i className="fa-solid fa-arrow-left me-2" />
          All Services
        </button>

        <div className={`tp-hero__content${mounted ? " tp-hero__content--in" : ""}`}>
          <div
            className="tp-hero__icon-wrap"
            style={{ "--tp-icon-bg": "rgba(255,255,255,0.15)" }}
          >
            <i className={`${service.icon} tp-hero__icon`} />
          </div>
          <h1 className="tp-hero__title">{service.title}</h1>
          <p className="tp-hero__desc">{service.description}</p>

          <div className="tp-hero__stats d-flex flex-wrap gap-3">
            <div className="tp-hero__stat">
              <i className="fa-solid fa-vial me-2" />
              <span>{service.tests.length} Tests Available</span>
            </div>
            <div className="tp-hero__stat">
              <i className="fa-solid fa-clock me-2" />
              <span>Same Day Results</span>
            </div>
            <div className="tp-hero__stat">
              <i className="fa-solid fa-certificate me-2" />
              <span>NABL Accredited</span>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. TestDetail
───────────────────────────────────────────── */
function TestDetail({ service }) {
  const sectionRef = useRef(null);
  // Use a plain object ref so we can hold an array
  // that is rebuilt each render without stale closures
  const itemRefs = useRef([]);
  // Clear the array on each render so old refs don't linger
  itemRefs.current = [];

  useEffect(() => {
    // --- test item observer ---
    const itemObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("tp-item--visible");
            itemObs.unobserve(e.target);
          }
        });
      },
      // threshold:0.05 — fires as soon as 5% is visible;
      // no rootMargin so nothing gets clipped on mobile
      { threshold: 0.05 }
    );

    itemRefs.current.forEach((el) => { if (el) itemObs.observe(el); });

    // --- heading observer ---
    const headObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("tp-detail-head--visible");
            headObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) headObs.observe(sectionRef.current);

    return () => {
      itemObs.disconnect();
      headObs.disconnect();
    };
  // service.id as dep: if React ever reuses this component
  // instance for a new slug, the observer re-runs with fresh refs
  }, [service.id]);

  return (
    <section className="tp-detail-section">
      <div className="tp-detail-inner">

        <div ref={sectionRef} className="tp-detail-head text-center mb-5">
          <span className="tp-eyebrow">What We Test</span>
          <h2 className="tp-section-title">
            Detailed Tests Under{" "}
            <span style={{ color: service.iconColor }}>{service.title}</span>
          </h2>
          <p className="tp-section-sub">{service.why}</p>
          <div className="tp-section-line" style={{ background: service.iconColor }} />
        </div>

        <Row className="g-4 g-lg-5 align-items-start">

          {/* Left — summary card */}
          <Col xs={12} lg={4}>
            <div
              className="tp-summary-card"
              style={{ "--tp-accent": service.iconColor, "--tp-accent-bg": service.iconBg }}
            >
              <div className="tp-summary-card__img-wrap">
                <img src={service.image} alt={service.title} loading="lazy" className="tp-summary-card__img" />
                <div className="tp-summary-card__img-badge">
                  <div className="tp-summary-card__icon-wrap">
                    <i className={`${service.icon} tp-summary-card__icon`} />
                  </div>
                  <span className="tp-summary-card__badge-title">{service.title}</span>
                </div>
              </div>

              <div className="tp-summary-card__body">
                <div className="tp-summary-card__stat-row">
                  <div className="tp-summary-card__stat">
                    <span className="tp-summary-card__stat-num">{service.tests.length}</span>
                    <span className="tp-summary-card__stat-label">Total Tests</span>
                  </div>
                  <div className="tp-summary-card__stat-divider" />
                  <div className="tp-summary-card__stat">
                    <span className="tp-summary-card__stat-num">24h</span>
                    <span className="tp-summary-card__stat-label">Report Ready</span>
                  </div>
                  <div className="tp-summary-card__stat-divider" />
                  <div className="tp-summary-card__stat">
                    <span className="tp-summary-card__stat-num">₹</span>
                    <span className="tp-summary-card__stat-label">Affordable</span>
                  </div>
                </div>

                <p className="tp-summary-card__desc">{service.description}</p>

                <a href="/contact" className="tp-summary-card__cta">
                  <i className="fa-solid fa-phone me-2" />
                  Book This Test
                </a>

                <a href="tel:7358798182" className="tp-summary-card__phone">
                  <i className="fa-solid fa-phone-volume me-2" />
                  7358798182
                </a>
              </div>
            </div>
          </Col>

          {/* Right — test list */}
          <Col xs={12} lg={8}>
            <div className="tp-tests-list">
              {service.tests.map((test, i) => (
                <div
                  key={`${service.id}-${i}`}
                  className="tp-item"
                  ref={(el) => { if (el) itemRefs.current[i] = el; }}
                  style={{
                    "--tp-item-delay": `${i * 70}ms`,
                    "--tp-accent":    service.iconColor,
                    "--tp-accent-bg": service.iconBg,
                  }}
                >
                  <div className="tp-item__left">
                    <div className="tp-item__num-wrap">
                      <span className="tp-item__num">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    {i < service.tests.length - 1 && <div className="tp-item__connector" />}
                  </div>

                  <div className="tp-item__content">
                    <div className="tp-item__header d-flex align-items-center gap-2 mb-2">
                      <div className="tp-item__icon-wrap">
                        <i className={`${service.icon} tp-item__icon`} />
                      </div>
                      <h3 className="tp-item__name mb-0">{test.name}</h3>
                    </div>
                    <p className="tp-item__detail mb-0">{test.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </Col>

        </Row>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. TestOthers
   key=slug on this component (set in parent)
   guarantees a full remount on every navigation,
   so the observer always starts fresh with the
   correct 6 cards and correct refs.
───────────────────────────────────────────── */
function TestOthers({ others }) {
  const navigate = useNavigate();
  const rowRef   = useRef(null);

  useEffect(() => {
    // Small timeout lets the grid paint before we observe,
    // preventing "already intersecting but missed" race
    const timer = setTimeout(() => {
      const cards = rowRef.current?.querySelectorAll(".tp-other-card");
      if (!cards || cards.length === 0) return;

      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("tp-other-card--visible");
              obs.unobserve(e.target);
            }
          });
        },
        { threshold: 0.05 }
      );

      cards.forEach((el) => obs.observe(el));

      // Store cleanup on the timer callback's scope
      rowRef._obs = obs;
    }, 60);

    return () => {
      clearTimeout(timer);
      rowRef._obs?.disconnect();
    };
  }, [others]); // re-runs whenever the others array reference changes

  const handleNavigate = (slug) => {
    window.scrollTo(0, 0);
    navigate(`/tests/${slug}`);
  };

  return (
    <section className="tp-others-section">
      <Container fluid="lg">
        <div className="text-center mb-5">
          <span className="tp-eyebrow">Explore More</span>
          <h2 className="tp-section-title">Other Diagnostic Services</h2>
        </div>

        <div className="tp-others-grid" ref={rowRef}>
          {others.map((svc, i) => (
            <div
              key={svc.id}
              className="tp-other-card"
              style={{
                "--tp-other-delay": `${i * 80}ms`,
                "--tp-icon-color":  svc.iconColor,
                "--tp-icon-bg":     svc.iconBg,
              }}
              onClick={() => handleNavigate(svc.slug)}
            >
              <div className="tp-other-card__img-wrap">
                <img src={svc.image} alt={svc.title} loading="lazy" className="tp-other-card__img" />
                <div className="tp-other-card__overlay" />
              </div>
              <div className="tp-other-card__body d-flex align-items-center gap-2">
                <div className="tp-other-card__icon-wrap flex-shrink-0">
                  <i className={`${svc.icon} tp-other-card__icon`} />
                </div>
                <span className="tp-other-card__title">{svc.title}</span>
              </div>
              <div className="tp-other-card__footer text-center">
                <span className="tp-other-card__link">
                  View Tests <i className="fa-solid fa-arrow-right ms-1" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}